<template>
  <div class="bank-sidebar">
    <BankAccountModal 
      v-if="selectedAccount"
      :account-data="selectedAccount"
      :show-modal="showAccountModal"
      @close="showAccountModal = false" 
    />

    <div class="bank-sidebar--selector">
      <BankAccountSelector />

      <div v-if="selectedAccount" class="bank-balance overview-box">
        <div class="overview-box-content">
          <div class="overview-title">{{ $gettext('BALANCE') }}</div>
          <div class="overview-amount">
            €{{ selectedAccount.balance | formatNumber }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="selectedAccount" class="bank-overview">
      <div class="overview-box">
        <div class="overview-box-content">
          <div class="overview-title">{{ $gettext('IBAN') }}</div>
          <div class="overview-concept">
            <span>{{ selectedAccount.iban | formatIBAN }}</span>
            <GekkoButton 
              @click="copyToClipboard(selectedAccount.iban)"
              @mouseleave.native="resetCopiedText()"
              id="copy-to-clipboard"
              icon="fa-regular fa-copy"
              variant="link"
              class="p-0 pl-2 text-blue border-0"
              size="sm"
            />
            <b-tooltip target="copy-to-clipboard" triggers="hover">
                <div class="tooltip-text">
                  {{ copyToClipboardText }}
                </div>
            </b-tooltip>
          </div>
        </div>
        <div class="overview-box-content">
          <div class="overview-title">{{ $gettext('ALIAS') }}</div>
          <div class="overview-concept">
            <span v-if="selectedAccount.alias">{{ selectedAccount.alias }}</span>
            <GekkoButton 
              :text="(!selectedAccount.alias) ? $gettext('Click to set an alias') : ''"
              @click="showEditAliasModal()"
              id="edit-account-alias"
              icon="fa-solid fa-pen-to-square"
              variant="link"
              class="p-0 pl-2 text-blue border-0"
              size="sm"
            />
            <b-tooltip target="edit-account-alias" triggers="hover">
              <div class="tooltip-text">
                {{ $gettext('Edit account alias') }}
              </div>
            </b-tooltip>
          </div>
        </div>
        <div class="overview-box-content">
          <div class="overview-title">{{ $gettext('STATUS') }}</div>
          <div class="overview-concept" :class="[selectedAccount.status.success ? 'text-brand' : 'text-warning']">
            {{ selectedAccount.status.status_text }}
          </div>
          <div class="overview-concept">
            {{ selectedAccount.status.status_message }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BankAccountSelector from "@/components/bank/ui/BankAccountSelector";
import BankAccountModal from "@/components/bank/ui/BankAccountModal";
import GekkoButton from "@/components/GekkoButton";
export default {
  name: "BankTransactionsSidebar",
  components: {
    BankAccountSelector,
    BankAccountModal,
    GekkoButton
  },
  data () {
    return {
      showCopiedText: false,
      showAccountModal: false
    }
  },
  computed: {
    ...mapGetters ({
      tinkStatus: "bankStore/getTinkStatus",
      selectedAccount: "bankStore/getSelectedAccount"
    }),
    copyToClipboardText () {
      return (this.showCopiedText) ? this.$gettext('Copied!') : this.$gettext('Copy to clipboard')
    },
    tinkStatusText() {
      if(this.selectedAccount.provider === "ENABLENOW"){
        if(!this.selectedAccount.external_consent_id){
          return this.$gettext("Not connected");
        }
        var expiry_date = new Date(this.selectedAccount.connection_expiry_date);
        if(expiry_date < new Date()){
          return this.$gettext("Access expired");
        }
        return this.$gettext("Connected");
      }
      if (this.tinkStatus)
        return this.tinkStatus.status;
      return this.$gettext("Not connected");
    }
  },
  methods: {
    copyToClipboard (data) {
      navigator.clipboard.writeText(data).then(() => {
        this.showCopiedText = true
      }, (err) => {
        console.error('Could not copy: ', err)
      })
    },
    resetCopiedText () {
      setTimeout(() => {
        this.showCopiedText = false
      }, 100)
    },
    showEditAliasModal () {
      this.showAccountModal = true
    }
  }
}
</script>

<style lang="scss" scoped>
.bank-sidebar {
  padding: 0.5rem;
  background: $colorBgSidebar;
  width: 30%;
  max-width: 300px;

  &--selector {
    border-bottom: 1px solid $colorBorderDash;
    .bank-account-selector {
      height: 66px; // debug
    }
    .bank-balance {
      &.overview-box {
        .overview-box-content {
          flex-direction: column;
          align-items: flex-start;
        }
        .overview-amount {
          display: block; 
          font-size: 38px;
          font-family: 'DINNextLTPro-Medium';
          font-weight: bold;
          line-height: 1;
          white-space: nowrap;
        }
      }
    }
  }
  .overview-box {
    padding: 2rem 0;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 2rem;
    &:not(:last-child) {
      border-bottom: 1px solid $colorBorderDash;
    }
    .overview-box-content {
      padding: 0 1rem;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      flex-direction: column;
      max-width: 100%;
    }
    .overview-title, .overview-concept {
      color: #7A7A7A;
      font-size: 14px;
      line-height: 1.3;
    }
    .overview-title {
      text-transform: uppercase;
      margin-bottom: 0.5rem;
    }
    .overview-concept {
      display: flex;
      align-items: baseline;
      color: $colorTextBase;
      width: 100%;
      span {
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: calc(100% - 1.5rem);
      }
      .btn { height: 14px; }
    }
  }
}
@media (max-width: '992px') {
  .bank-sidebar {
    &--selector {
      .bank-balance {
        &.overview-box {
          .overview-amount {
            font-size: 34px;
          }
        }
      }
    }
  }
}
@media (max-width: '768px') {
  .bank-sidebar {
    width: 100%;
    max-width: 100%;
    padding-bottom: 0;
    border-bottom: 1px solid $colorBorderDash;
    &--selector {
      display: flex;
      flex-wrap: wrap;
      padding-bottom: 0.5rem;
      gap: 1rem;
      .bank-account-selector {
        min-width: 260px;
        width: 50%;
      }
      .bank-balance {
        &.overview-box {
          padding: 0;
          display: flex;
          justify-content: center;
          .overview-box-content {
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
          }
          .overview-title {
            margin-bottom: 0.5rem;
          }
          .overview-amount {
            font-size: 34px;
          }
        }
      }
    }
    .bank-overview {
      .overview-box {
        padding: 1rem 0;
        flex-direction: row;
        gap: 1rem;
        .overview-box-content {
          justify-content: center;
          align-items: flex-start;
          flex-direction: column;
          &.overview-actions {
            width: 100%;
            flex-direction: row;
            justify-content: space-evenly;
            flex-wrap: wrap;
          }
        }
        .overview-title, .overview-concept {
          margin-bottom: 0rem;
        }
        .overview-title {
          margin-bottom: 0.5rem;
        }
      }
    }
  }
}

@media (max-width: '576px') {
  .bank-sidebar {
    &--selector {
      .bank-account-selector {
        width: 100%;
      }
      .bank-balance {
        &.overview-box {
          .overview-amount {
            font-size: 30px;
          }
        }
      }
    }
  }
}
</style>